import {
  Box,
  Typography,
  FormControl,
  Button,
  Grid,
  Autocomplete,
  TextField,
  useTheme
} from '@mui/material';
import Rating from '@mui/material/Rating';
import {Formik, Form} from 'formik';
import {useAppSelector} from '@/redux-store/store';
import {useTranslation} from 'react-i18next';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {updateUserData} from '@/rest/user/update-user-data';
import {updateUserSports} from '@/redux-store/slices/user-infos';
import {useDispatch} from 'react-redux';
import {UserFormData} from '@/types/user';
import TypographyTitle from '@/components/ui/typography-title';
import {findSports} from '@/rest/sports/find-sports';
import LoadingScreen from '@/pages/loading-screen';

type Labels = {
  [key: string]: string;
};

interface Sport {
  _id: string;
  key: string;
  labels: Labels;
  picture: string;
}

interface SportLevel extends Sport {
  level: number;
}

interface SportsFormProps {
  onCancel?: () => void;
  onSubmit?: (editedSports: unknown) => void;
  onError?: (error: Error) => void;
}

interface ServerSportLevel {
  level: number;
  sport: Sport;
}

const SportsForm: React.FC<SportsFormProps> = ({
  onCancel,
  onSubmit,
  onError
}) => {
  const {t, i18n} = useTranslation();

  const theme = useTheme();

  const userData = useAppSelector((state) => state.userInfos.userInfos);

  const [loadedSports, setLoadedSports] = useState<Sport[]>([]);
  const [selectedSports, setSelectedSports] = useState<SportLevel[]>([]);

  const [initialReduxSports, setInitialReduxSports] = useState<SportLevel[]>(
    []
  );

  const dispatch = useDispatch();

  const handleSportChange = useCallback(
    (newValue: Sport[] | null) => {
      if (newValue) {
        const removedSports = selectedSports.filter(
          (sport) => !newValue.find((newSport) => newSport._id === sport._id)
        );

        const addedSports = newValue.filter(
          (newSport) =>
            !selectedSports.find((sport) => sport._id === newSport._id)
        );

        setSelectedSports((prevState) =>
          prevState
            .filter((sport) => !removedSports.includes(sport))
            .concat(
              addedSports.map((sport) => ({
                ...sport,
                level: 1
              }))
            )
        );
      }
    },
    [selectedSports]
  );

  const handleLevelChange = useCallback(
    (id: string, newValue: number | null) => {
      if (newValue !== null) {
        setSelectedSports((prevState) =>
          prevState.map((sport) =>
            sport._id === id ? {...sport, level: newValue} : sport
          )
        );
      }
    },
    []
  );

  const removeSport = useCallback((id: string) => {
    setSelectedSports((prevState) =>
      prevState.filter((sport) => sport._id !== id)
    );
  }, []);

  const handleSubmit = useCallback(() => {
    const sportsValues: UserFormData = {
      _id: userData._id,
      geolocation: userData.location
        ? {
            coordinates: [userData.location.lng, userData.location.lat] as [
              number,
              number
            ],
            type: 'Point'
          }
        : null,
      sports: selectedSports.map((sport: SportLevel) => ({
        sport: sport._id,
        level: sport.level
      }))
    };

    updateUserData(sportsValues)
      .then((response) => {
        if (response) {
          const updatedSports = response.sports.map(
            (sport: ServerSportLevel) => ({
              ...sport,
              _id: sport.sport._id,
              key: sport.sport.key,
              labels: sport.sport.labels,
              picture: sport.sport.picture,
              level: sport.level
            })
          );

          dispatch(updateUserSports(updatedSports));
          setInitialReduxSports(updatedSports);

          if (onSubmit) {
            onSubmit(response);
          }
        }
      })
      .catch((error) => {
        console.error('Error: ', error);
        if (onError) {
          onError(error as Error);
        }
      });
  }, [
    dispatch,
    onError,
    onSubmit,
    selectedSports,
    userData._id,
    userData.location
  ]);

  const sortedSportsFromRedux = useMemo(() => {
    const sortedSports = [...loadedSports];
    sortedSports.sort((a, b) => {
      const labelA = a.labels?.[i18n.language] || '';
      const labelB = b.labels?.[i18n.language] || '';
      if (!labelA || !labelB) {
        return 0;
      }
      return labelA.localeCompare(labelB);
    });
    return sortedSports;
  }, [loadedSports, i18n.language]);

  useEffect(() => {
    if (userData.sports && loadedSports.length) {
      const initialSportsData = userData.sports.map((sport) => {
        const fullSportData = sortedSportsFromRedux.find(
          (s) => s._id === sport.sport._id
        );

        return fullSportData
          ? {
              ...fullSportData,
              _id: sport.sport._id,
              level: sport.level
            }
          : {
              _id: sport.sport._id,
              key: sport.sport.key,
              labels: {en: 'default', de: 'default'},
              picture: '',
              level: sport.level
            };
      });

      setSelectedSports(initialSportsData);
      setInitialReduxSports(initialSportsData);
    }
  }, [loadedSports, sortedSportsFromRedux, userData.sports]);

  useEffect(() => {
    const fetchSports = async () => {
      if (loadedSports.length === 0) {
        const sports = await findSports();
        if (sports) {
          setLoadedSports(Object.values(sports));
        }
      }
    };

    fetchSports();
  }, [loadedSports]);

  if (loadedSports.length === 0) {
    return <LoadingScreen />;
  }
  return (
    <Formik
      initialValues={{selectedSports: selectedSports}}
      onSubmit={handleSubmit}
    >
      {({resetForm}) => (
        <Form>
          {/* <Typography
            variant={isSmDown ? 'h6' : 'h5'}
            sx={{
              pb: 1,
              justifyContent: 'center',
              display: { xs: 'flex' },
              marginTop: 2,
              marginBottom: 2
            }}
          >
            {t('my_interests')}
          </Typography> */}
          <TypographyTitle textKey="my_interests" />
          <Grid container spacing={2} marginTop={2} justifyContent="center">
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  minWidth: 120,
                  maxWidth: 500,
                  marginBottom: 2,
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <FormControl fullWidth>
                  <Autocomplete
                    multiple
                    noOptionsText={t('no_options')}
                    disableClearable
                    // clearOnBlur={false}
                    // clearOnEscape={false}
                    options={sortedSportsFromRedux}
                    getOptionLabel={(option) => option.labels[i18n.language]}
                    value={selectedSports}
                    isOptionEqualToValue={(option, value) =>
                      option._id === value._id
                    }
                    onChange={(event, newValue) => handleSportChange(newValue)}
                    renderInput={(params) => (
                      <TextField {...params} label={t('interests')} />
                    )}
                  />
                </FormControl>
              </Box>
            </Grid>
          </Grid>
          <Grid container spacing={2} my={1}>
            <Grid item xs={12}>
              <Typography
                variant="h6"
                sx={{
                  justifyContent: 'center',
                  display: {xs: 'flex'},
                  marginBottom: 2
                }}
              >
                {t('interests_details')}
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            {selectedSports.map((sport) => (
              <Grid item xs={12} sm={6} key={sport._id}>
                <Box
                  component="fieldset"
                  borderColor="transparent"
                  key={sport._id}
                  sx={{
                    display: 'flex',
                    flexDirection: {xs: 'column', sm: 'row'},
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    textAlign: 'center',
                    position: 'relative'
                  }}
                >
                  <Box sx={{flexGrow: 1}}>
                    <Typography component="legend">
                      {i18n.language === 'en'
                        ? sport.labels.en
                        : sport.labels.de}
                    </Typography>
                    <Rating
                      name={sport._id}
                      value={sport.level}
                      onChange={(event, newValue) =>
                        handleLevelChange(sport._id, newValue)
                      }
                    />
                    <Typography>
                      {sport.level === 1 && t('interest_newbie')}
                      {sport.level === 2 && t('interest_beginner')}
                      {sport.level === 3 && t('interest_normal')}
                      {sport.level === 4 && t('interest_advanced')}
                      {sport.level === 5 && t('interest_pro')}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: {xs: 'absolute', sm: 'initial'},
                      right: {xs: 0, sm: 'initial'},
                      bottom: {xs: 0, sm: 'initial'},
                      alignSelf: {xs: 'flex-end', sm: 'center'}
                    }}
                  >
                    <Button
                      onClick={() => removeSport(sport._id)}
                      sx={{
                        color: theme.palette.text.primary
                      }}
                    >
                      X
                    </Button>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2} my={3}>
            <Grid
              item
              xs={12}
              sm={4}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: {xs: 1, sm: 0},
                order: {xs: 1, sm: 0}
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  resetForm();
                  if (onCancel) {
                    onCancel();
                  }
                }}
                sx={{
                  width: {xs: '50%', sm: '80%'}
                }}
              >
                {t('cancel')}
              </Button>
            </Grid>

            <Grid
              item
              xs={12}
              sm={4}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: {xs: 1, sm: 0}
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setSelectedSports(initialReduxSports);
                }}
                sx={{
                  width: {xs: '50%', sm: '80%'}
                }}
              >
                {t('reset')}
              </Button>
            </Grid>

            <Grid
              item
              xs={12}
              sm={4}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mb: {xs: 1, sm: 0},
                order: {xs: -1, sm: 0}
              }}
            >
              <Button
                variant="contained"
                color="primary"
                type="submit"
                sx={{
                  width: {xs: '50%', sm: '80%'}
                }}
              >
                {t('save')}
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default SportsForm;
